import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { ProductService } from 'src/app/core/api/product/product.service';
import { RewardService } from 'src/app/core/api/reward/reward.service';
import { TopHeaderService } from 'src/app/core/services/top-header/top-header.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { ChipContent } from 'src/app/shared/models/chip-content/chip-content';
import { ChipContentOption } from 'src/app/shared/models/chip-content/chip-content-option';
import { FilterType } from 'src/app/shared/models/constants/filter-type';
import { SortOption } from 'src/app/shared/models/constants/sort-option';
import { FamilyDTO } from 'src/app/shared/models/dtos/responses/family/family';
import { RewardResponseDTO } from 'src/app/shared/models/dtos/responses/reward-response-dto/reward-response-dto';

@Component({
  selector: 'app-rewards-list',
  templateUrl: './rewards-list.component.html',
  styleUrls: ['./rewards-list.component.scss'],
})

/**
 * Display main components of rewards page
 */
export class RewardsListComponent implements OnInit, OnDestroy {
  @Input() rewardList: RewardResponseDTO[] = [];
  labels = {
    rewards: 'rewards',
    notFound: 'reward-not-list',
    notFoundDescription: 'reward-not-list-description',
  };
  loading = false;
  private page = 0;
  private pageSize = 25;
  private defaultSort = SortOption.MOST_CLAIMED;
  private subsink = new Subscription();
  private apiCall?: Subscription;

  sortByChip = new ChipContent(
    undefined,
    'most-reclaimed',
    FilterType.REWARDS_SORT_BY,
    true,
    'expand_more',
    [
      new ChipContentOption(SortOption.MOST_CLAIMED, 'most-reclaimed', 'sort'),
      new ChipContentOption(
        SortOption.LOW_TO_HIGH,
        'price-low-to-high',
        'sort'
      ),
      new ChipContentOption(
        SortOption.HIGH_TO_LOW,
        'price-high-to-low',
        'sort'
      ),
      new ChipContentOption(SortOption.A_TO_Z, 'alphabetical-a-to-z', 'sort'),
      new ChipContentOption(SortOption.Z_TO_A, 'alphabetical-z-to-a', 'sort'),
    ]
  );

  filterByFamily = new ChipContent(
    undefined,
    'all-series',
    FilterType.SERIES_FILTER,
    false,
    'expand_more',
    [new ChipContentOption(FilterType.SERIES_FILTER, 'all-series', 'filter')]
  );

  private featuredChip = new ChipContent(
    undefined,
    'my-printers',
    FilterType.REWARDS_MY_PRINTERS,
    false
  );
  private customerServiceChip = new ChipContent(
    undefined,
    'c-service',
    FilterType.REWARDS_CUSTOMER_SERVICE,
    false
  );
  private productsChip = new ChipContent(
    undefined,
    'products',
    FilterType.REWARDS_PRODUCTS,
    false
  );

  chipContents = [
    this.sortByChip,
    this.filterByFamily,
    this.featuredChip,
    this.customerServiceChip,
    this.productsChip,
  ];

  filters: any = {
    opportunities: false,
    cservice: false,
    products: false,
    sort: this.defaultSort,
    myPrinters: false,
    series: undefined,
  };
  COLOMBIA_COUNTRY_ID = 2;

  /**
   * @constructor
   */
  constructor(
    private translationService: TranslationService,
    public rewardService: RewardService,
    private productService: ProductService,
    public topHeaderService: TopHeaderService
  ) {}

  /**
   * ng on init
   */
  ngOnInit(): void {
    this.translationService.translateObject(this.labels);
    this.pullRewards();
    this.getPrintersFamily();
  }

  /**
   * cleans up resources
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * Loads more items
   */
  pullRewards() {
    this.apiCall?.unsubscribe();
    this.loading = true;
    const params = {
      page: this.page,
      pageSize: this.pageSize,
      sort: this.filters.sort,
    };
    this.assignParams(params);
    const rewards$ = this.rewardService
      .getRewards(params)
      .subscribe((rewards) => {
        if (rewards.length > 0) {
          this.page++;
        }
        this.rewardList.push(...rewards);
      });
    rewards$.add(() => {
      this.loading = false;
    });
    this.apiCall = rewards$;
    this.subsink.add(rewards$);
  }

  /**
   * Generate filter params and emit it
   * @param {ChipContent} element
   */
  getFilter(element: ChipContent) {
    if (element.filterType === FilterType.REWARDS_FEATURED) {
      element.isActive = !element.isActive;
      this.filters.opportunities = !this.filters.opportunities;
      this.resetList();
      this.pullRewards();
    }

    if (element.filterType === FilterType.REWARDS_MY_PRINTERS) {
      element.isActive = !element.isActive;
      this.filters.myPrinters = !this.filters.myPrinters;
      this.resetList();
      this.pullRewards();
    }

    if (element.filterType === FilterType.REWARDS_CUSTOMER_SERVICE) {
      element.isActive = !element.isActive;
      this.filters.cservice = !this.filters.cservice;
      this.resetList();
      this.pullRewards();
    }

    if (element.filterType === FilterType.REWARDS_PRODUCTS) {
      element.isActive = !element.isActive;
      this.filters.products = !this.filters.products;
      this.resetList();
      this.pullRewards();
    }

    this.filters = { ...this.filters };
  }

  /**
   * Captures all the families from registered equipment
   */
  getPrintersFamily() {
    this.productService.getBusinessEquipments().subscribe((equipments) => {
      let allFamilies = equipments
        .map((e) => e.family)
        .filter((f): f is FamilyDTO => !!f);
      allFamilies = Array.from(
        new Map(allFamilies.map((f) => [f.id, f])).values()
      );

      for (const family of allFamilies) {
        this.filterByFamily.options?.push(
          new ChipContentOption(family.id.toString(), family.name, 'filter')
        );
      }

      this.chipContents = [
        this.sortByChip,
        this.filterByFamily,
        this.featuredChip,
        this.customerServiceChip,
        this.productsChip,
      ];
    });
  }

  /**
   * Raises the filter with sort by option selected
   * @param {any} event
   * @param {ChipContent} element
   */
  getOption(event: ChipContentOption) {
    if (event.type === 'sort') {
      const previousSort = this.filters.sort;
      this.filters.sort = event.key as SortOption;
      if (previousSort !== this.filters.sort) {
        this.resetList();
        this.pullRewards();
      }
      this.sortByChip.isActive = true;
    } else if (event.type === 'filter') {
      this.filterByFamily.isActive = true;
      if (event.key === 'series') {
        this.filters.series = undefined;
      } else {
        this.filters.series = parseInt(event.key);
      }
      this.resetList();
      this.pullRewards();
    }

    this.filters = { ...this.filters };
  }

  /**
   * Assign filter params to be fetched
   * @param {Record<string, any>} params
   */
  private assignParams(params: Record<string, any>) {
    if (this.filters.opportunities) params['opportunities'] = true;
    if (this.filters.cservice) params['cservice'] = true;
    if (this.filters.products) params['products'] = true;
    if (this.filters.myPrinters) params['myPrinters'] = true;

    if (
      this.filters.series !== undefined &&
      !Number.isNaN(this.filters.series)
    ) {
      params['series'] = this.filters.series;
    }
  }

  /**
   * Resets rewards list
   */
  private resetList() {
    this.rewardList = [];
    this.page = 0;
  }
}
